import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { StructuredText } from 'react-datocms';
import { toast } from 'react-toastify';

import AuthModal from '@/components/auth/AuthModal/AuthModal';
import Button from '@/components/common/Button/Button';
import Loader from '@/components/common/Loader/Loader';
import MobileProductInfoCard from '@/components/common/MobileProductInfoCard/MobileProductInfoCard';
import ProductShippingInfo from '@/components/common/ProductShippingInfo/ProductShippingInfo';
import QuantityButton from '@/components/common/QuantityButtonV2/QuantityButtonV2';
import { RecommendationProductsContainer } from '@/components/common/RecommendationProductsContainer/RecommendationProductsContainer';
import ShippingEducation from '@/components/common/ShippingEducation/ShippingEducation';
import { PlanSwapBanner } from '@/components/menu/PlanSwapBanner/PlanSwapBanner';
import ProductLikePill from '@/components/menu/ProductLikePill/ProductLikePill';
import Breadcrumb from '@/components/menuV2/BreadCrumb/BreadCrumb';
import { BenefitTagsContainer } from '@/components/productDetails/BenefitTagsContainer/BenefitTagsContainer';
import { ImagesSwiper } from '@/components/productDetails/ImagesSwiper/ImagesSwiper';
import { Price } from '@/components/productDetails/Price/Price';
import { ProductInfo } from '@/components/productDetails/ProductInfo/ProductInfo';
import { SectionsContainer } from '@/components/productDetails/SectionsContainer/SectionsContainer';
import { SubProductsContainer } from '@/components/productDetails/SubProductsContainer/SubProductsContainer';
import SpiceBadge from '@/components/SpiceInfo/SpiceBadge';
import { PRODUCT_PDP_SELECTOR, SLUG_SELECTOR } from '@/graphql/selectors';
import { useAuthModal } from '@/hooks/useAuthModal';
import { useScrollYPosition } from '@/hooks/useScrollYPosition';
import { Meta } from '@/layouts/Meta';
import menuBuilderData from '@/mock-data/menu-builder.json';
import type { ProductModelType } from '@/models/api';
import { ProductStatus } from '@/models/api';
import type { OrderPlan } from '@/models/cart/order-plan';
import { initializeStore, useStore } from '@/models/root-store';
import { AUTH_NAVIGATION_TARGET } from '@/shared/constant';
import { Main } from '@/templates/Main';
import { cacheAllProductsPDPData, getCachedProduct } from '@/utils/build-cache';
import { Action, DRAFT_CART_ACTIONS, Mode } from '@/utils/constants';
import {
  trackProductViewed,
  trackRecommendationViewed,
} from '@/utils/track/tracker.helper';

type ProductDetailsProps = {
  product: ProductModelType | null;
};

const ProductDetails: NextPage<ProductDetailsProps> = observer(
  ({ product }: ProductDetailsProps) => {
    const router = useRouter();
    const [visibleLoaded, setVisibleLoaded] = useState(false);
    const [isVisible, setIsVisible] = useState(product?.visible ?? false);
    const [showProductInfoCard, setShowProductInfoCard] = useState(false);
    const {
      cartStore: { cart, draftCart },
      productStore: {
        productsLookup,
        checkProductsVisible,
        isProductRestricted,
        deliveryAddress,
        search: { resetSearch },
      },
      generalStore: {
        setAuthModalOpen,
        getContentByKey,
        setRestrictedProductsCheck,
      },
      customerStore,
    } = useStore();
    const isRestricted =
      product && deliveryAddress ? isProductRestricted(product.sku) : false;

    const recommendationContent = getContentByKey('product-recommendation');
    const visibilityContent = getContentByKey('product-visibility-pdp');
    const { slug, action, target } = router.query;
    const [isAuthModalOpen] = useAuthModal();
    const { spiceRating = '' } = product?.nutritionalInfo || {};
    const isDraftCartAction = DRAFT_CART_ACTIONS.includes(action as Action);
    const isFullPDP = action !== Action.SWAP_ITEM;
    const cartToUse =
      draftCart.mode === Mode.EDIT_SUBSCRIPTION || isDraftCartAction
        ? draftCart
        : cart;
    const showRecurringEducation = isFullPDP;
    const notShowRecommendationsOrBreadcrumb = isDraftCartAction;
    const hideNavFooter = action === Action.NEW_PLAN;
    const path = router.pathname.replace('[slug]', String(slug));
    const guestViewable =
      (product &&
        !product.visible &&
        _.find(product.attributes, { name: 'guestViewable' })?.value) ||
      false;

    useEffect(() => {
      if (customerStore.isLoggedIn) {
        customerStore.getCustomerProfile();
      }
    }, [customerStore.isLoggedIn]);

    useEffect(() => {
      if (!cartToUse.menuBuilderPlan) {
        cartToUse.initOrderPlan(menuBuilderData);
      }
    }, [cartToUse.menuBuilderPlan]);

    useEffect(() => {
      if (!router.isReady) return;
      if (!product || (visibleLoaded && !isVisible && !guestViewable)) {
        router.push('/menu/meals');
        return;
      }
      if (!visibleLoaded && !isVisible) {
        const skus = [product.sku];
        checkProductsVisible(skus).then((visible: boolean) => {
          setIsVisible(visible);
          setVisibleLoaded(true);
        });
      }
    }, [product, router.isReady, visibleLoaded, isVisible]);

    if (!product) {
      return null;
    }

    const showLoader = !isVisible && !guestViewable;

    const { menuBuilderPlan } = cartToUse;
    const { draftPlanLookup } = draftCart;
    const buttonLabel =
      cartToUse.mode === Mode.EDIT_SUBSCRIPTION
        ? 'Add to order'
        : 'Add to cart';

    const getMenuHierarchySections = () => {
      return _.filter(
        product.cms?.menuHierarchy,
        (category) => category.sections && category.sections.length > 0,
      )[0];
    };

    const plansLookup = draftCart?.plansLookup;
    const planIdToView: string = draftCart?.planIdToView;
    const selectedPlan: OrderPlan =
      (draftPlanLookup && draftPlanLookup[planIdToView]) ||
      (plansLookup && plansLookup[planIdToView]);
    const route: string = selectedPlan?.planRoute || '';

    const item = productsLookup[product.sku];
    const isWlp = item && item.isWlpItem;
    const itemDiscount =
      item && item.getDiscountedPrice(menuBuilderPlan?.discount);
    const tags = product.cms?.tags ? product.cms?.tags : [];
    const dietaryTags = product.cms?.dietaryTags
      ? product.cms?.dietaryTags
      : [];
    const images = product.cms?.displayPageImages;
    const sections = getMenuHierarchySections();

    const { promotionTotal } = product;

    const quantity = menuBuilderPlan?.productQtyBySku[product.sku]?.qty || 0;

    const handleRestrictedCheck = () => {
      setRestrictedProductsCheck([product.sku]);
    };
    const handleAddProduct = () => {
      if (guestViewable && !customerStore.isLoggedIn) {
        sessionStorage.setItem(AUTH_NAVIGATION_TARGET, `/products/${slug}`);
        router.push('/signin');
        toast.info(
          visibilityContent?.notLoggedIn || 'Please sign in to continue',
        );
        return;
      }
      if (customerStore.isLoggedIn && !isVisible) {
        toast.warn(visibilityContent?.notAllowed || 'Product is not available');
        return;
      }
      menuBuilderPlan?.increaseLineItem(product, 1, 'pdp');
    };

    const handleSwapItem = () => {
      try {
        const selectedProduct = productsLookup[product.sku];
        const originalProduct = productsLookup[target as string];
        if (selectedPlan.isSavedTimePlan) {
          selectedPlan.swapProduct(selectedProduct, originalProduct);
        } else {
          const { x, y } = selectedPlan.selectedItemPosition;
          selectedPlan.swapLineItem(selectedProduct, x, y);
        }
        resetSearch();
        // navigate to the plan
        router.push({
          pathname: route,
          query: {
            planId: draftCart.planIdToView,
            ...(draftCart.mode && { mode: draftCart.mode }),
          },
        });
      } catch (error) {
        // ignore error
      }
    };

    const handleDeleteProduct = () => {
      menuBuilderPlan?.decreaseLineItem(product, 'pdp');
    };

    let bundleServingsPerPackage: number | undefined;
    if (product.hasSubProducts && product.subProducts?.length === 1) {
      const {
        qty,
        product: {
          nutritionalInfo: { servingsPerPackage },
        },
      } = product.subProducts[0];
      bundleServingsPerPackage = servingsPerPackage * qty;
    }

    const renderButtons = () => {
      switch (action) {
        case Action.SWAP_ITEM:
          return (
            <div className="p-4 md:py-2">
              <Button
                theme={isRestricted ? 'disabled' : 'primary'}
                onClick={() => handleSwapItem()}
                disabled={isRestricted}
                className="rounded-32 font-interMedium leading-4 md:py-[15px] md:font-interMedium"
              >
                <div className="px-12 py-1 text-base md:px-8 md:py-0 md:text-lg lg:px-16">
                  Swap Item
                </div>
              </Button>
            </div>
          );
        case Action.ADD_ITEM:
        case Action.ADD_EXTRA:
          return (
            <div className="p-4 md:py-2">
              <Button
                theme={isRestricted ? 'disabled' : 'primary'}
                onClick={() => handleSwapItem()}
                className="rounded-32 font-interMedium leading-4 md:py-[15px] md:font-interMedium"
              >
                <div className="px-12 py-1 text-base md:px-8 md:py-0 md:text-lg lg:px-16">
                  add to plan
                </div>
              </Button>
            </div>
          );
        case Action.VIEW:
        case Action.VIEW_WLP:
        case Action.NEW_PLAN:
          return null;
        default:
          if (quantity > 0) {
            return (
              <>
                <div className="w-full p-4 md:py-2">
                  <QuantityButton
                    handleAddProduct={handleAddProduct}
                    handleDeleteProduct={handleDeleteProduct}
                    disabled={isRestricted}
                    qty={quantity}
                    size="large"
                    page="pdp"
                  />
                </div>
              </>
            );
          }
          return (
            <>
              <div className="w-full p-4 md:w-auto md:py-2">
                <div className="flex justify-end">
                  <Button
                    theme={isRestricted ? 'disabled' : 'primary'}
                    type="button"
                    onClick={handleAddProduct}
                    disabled={isRestricted}
                    className="w-full"
                  >
                    <span className="px-20 py-1 font-interMedium text-base leading-none md:font-interMedium md:text-xl md:leading-[30px]">
                      {buttonLabel}
                    </span>
                  </Button>
                </div>
              </div>
            </>
          );
      }
    };
    const scrollYPosition = useScrollYPosition();
    const productInfoRef: any = useRef(null);
    const isProductInfoScrolling =
      scrollYPosition >=
      (productInfoRef.current && productInfoRef.current.offsetTop);

    useEffect(() => {
      if (productInfoRef.current && isProductInfoScrolling) {
        setShowProductInfoCard(true);
      } else {
        setShowProductInfoCard(false);
      }
    }, [isProductInfoScrolling]);

    const backToPlan = () => {
      router.back();
    };

    const recommendations = _.filter(
      product.recommendations,
      ({ sku }) => productsLookup[sku],
    );

    useEffect(() => {
      if (!product) return;
      try {
        if (
          !_.isEmpty(product.recommendations) &&
          !notShowRecommendationsOrBreadcrumb
        ) {
          trackRecommendationViewed(
            'product_recommendations',
            'recommendations',
            'pdp',
          );
        }
        trackProductViewed(product);
      } catch (error) {
        // ignore error
      }
    }, [product.sku]);

    const { isNewCustomer } = customerStore.customer;
    const recommendationTitle = isNewCustomer
      ? recommendationContent?.newTitle || 'Customer favourites'
      : recommendationContent?.returnTitle || 'Picked for you';
    return (
      <Main
        meta={
          <Meta
            title={product.cms?.seoTitle || product.name}
            description={
              product.cms?.seoDescription || product.cms?.description
            }
            ogImage={product.cms?.ogImage?.url || null}
            canonical={`${process.env.NEXT_PUBLIC_SITE_URL}/products/${slug}`}
            noIndexing={product.visible === false}
          />
        }
        showFooter={!hideNavFooter}
        showMegaNav={!hideNavFooter}
      >
        {showLoader ? (
          <Loader />
        ) : (
          <>
            {action && cartToUse.mode !== Mode.EDIT_SUBSCRIPTION && (
              <PlanSwapBanner action={action as Action} back={backToPlan} />
            )}
            <div className="container p-0 md:px-10 lg:px-28 lg:pb-5">
              <div className="product-detail-page relative mt-1 flex flex-col md:mt-8 md:flex-row">
                {/* DESKTOP - Images */}
                <div className="hidden w-5/12 md:block">
                  <ImagesSwiper product={product} />
                  {!_.isEmpty(recommendations) &&
                    !notShowRecommendationsOrBreadcrumb && (
                      <RecommendationProductsContainer
                        productRecommendationTitle={recommendationTitle}
                        plan={menuBuilderPlan}
                        products={_.map(product.recommendations, 'sku')}
                      />
                    )}
                </div>

                <div className="w-full md:w-7/12">
                  <div className="p-4 md:py-0 md:pl-4 lg:pl-16">
                    {!notShowRecommendationsOrBreadcrumb && (
                      <Breadcrumb
                        menuHierarchies={product.cms?.defaultBreadcrumb}
                      />
                    )}
                    <h1 className="pb-1 pt-3 font-interSemibold text-3xl leading-7 md:pt-0 md:text-6xl md:leading-[50px]">
                      {product.cms?.primaryName || product.name}
                    </h1>

                    <h3 className="pb-5 font-interMedium text-base text-grey-midnight md:pb-3 md:text-xl">
                      {product.cms?.secondaryName}
                    </h3>

                    {/* DESKTOP - Weight */}
                    <div className="hidden py-1 md:mb-5 md:flex">
                      {action === Action.VIEW_WLP || isWlp ? (
                        <div></div>
                      ) : (
                        <Price
                          discount={menuBuilderPlan?.discount}
                          discountedAmount={itemDiscount}
                          grossPrice={product.grossPrice || 0}
                          smallTile={false}
                          hasSubProducts={product.hasSubProducts || false}
                          isBulkDiscountable={product.isBulkDiscountable}
                          promotionTotal={promotionTotal}
                        />
                      )}

                      {product.hasSubProducts ? (
                        <div className="ml-3 flex">
                          <ProductLikePill
                            view="large"
                            sku={product.sku}
                            selected={false}
                          />
                        </div>
                      ) : (
                        <div className="flex w-full justify-between px-2 font-interMedium text-2xl text-grey-dark">
                          <div className="ml-3 flex">
                            <ProductLikePill
                              view="large"
                              sku={product.sku}
                              selected={false}
                            />
                          </div>
                          {product.nutritionalInfo?.size}
                          {product.nutritionalInfo?.unit}
                        </div>
                      )}
                    </div>

                    {/* MOBILE - Price & Weight */}
                    <div className="flex justify-between p-0 md:hidden md:p-2">
                      {action === Action.VIEW_WLP || isWlp ? (
                        <div></div>
                      ) : (
                        <Price
                          discount={menuBuilderPlan?.discount}
                          discountedAmount={itemDiscount}
                          grossPrice={product.grossPrice || 0}
                          smallTile={false}
                          hasSubProducts={product.hasSubProducts || false}
                          promotionTotal={promotionTotal}
                          isBulkDiscountable={product.isBulkDiscountable}
                        />
                      )}

                      {product.hasSubProducts ? (
                        <div className="ml-3 flex">
                          <ProductLikePill
                            view="large"
                            sku={product.sku}
                            selected={false}
                          />
                        </div>
                      ) : (
                        <div className="flex justify-around">
                          <div className="flex">
                            <ProductLikePill
                              view="large"
                              sku={product.sku}
                              selected={false}
                            />
                          </div>
                          <div className="ml-3 flex font-interMedium">
                            {product.nutritionalInfo?.size}
                            {product.nutritionalInfo?.unit}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* MOBILE - Images */}
                  {images && (
                    <div className="mb-5 block w-full md:hidden">
                      <ImagesSwiper product={product} />
                    </div>
                  )}

                  {/* Description */}
                  <div className="flex flex-col-reverse px-4 md:flex-col md:pl-4 md:pt-0 lg:pl-16">
                    <article className="py-3">
                      <StructuredText data={product.cms?.description} />
                    </article>
                  </div>

                  {/* Benefits */}
                  {dietaryTags && tags && (
                    <div className="mb-3 mt-4 px-4 md:pl-4 lg:pl-16">
                      <BenefitTagsContainer tags={[...dietaryTags, ...tags]} />
                    </div>
                  )}
                  {/* Spice Rating */}
                  {spiceRating && spiceRating !== 'NA' && (
                    <div className="mb-5 px-4 md:pl-4 lg:pl-16">
                      <section className="flex max-w-[589px] flex-wrap items-center gap-2 border-b border-stone-300 pb-4 pr-1.5 text-sm leading-none tracking-normal text-black">
                        <p className="font-interMedium text-sm">
                          Spice Rating:
                        </p>
                        <SpiceBadge
                          text={spiceRating.toUpperCase()}
                          icon={spiceRating.toLowerCase()}
                        />
                      </section>
                    </div>
                  )}

                  {/* Desktop Buttons for Menu, swap and viewing */}
                  <div className="hidden pl-10 md:flex md:pl-0 lg:pl-10">
                    <div className="flex flex-col items-center">
                      {renderButtons()}
                      {showRecurringEducation && <ProductShippingInfo />}
                      {isRestricted && (
                        <div className="font-interMedium text-xs">
                          Unavailable for delivery to
                          <span
                            className="ml-2 text-attention underline"
                            onClick={handleRestrictedCheck}
                          >
                            {deliveryAddress}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Sections */}
                  <div ref={productInfoRef}>
                    <SectionsContainer
                      sections={sections?.sections}
                      showButton={!action}
                    />
                  </div>
                  <div className="p-2 md:hidden md:pl-4 lg:pl-16">
                    {!_.isEmpty(recommendations) &&
                      !notShowRecommendationsOrBreadcrumb && (
                        <RecommendationProductsContainer
                          productRecommendationTitle={recommendationTitle}
                          plan={menuBuilderPlan}
                          products={_.map(product.recommendations, 'sku')}
                        />
                      )}
                  </div>

                  {/* Nutrition Accordions */}
                  {!product.hasSubProducts && (
                    <div className="p-4 md:pl-4 lg:pl-16">
                      <ProductInfo
                        nutritionalInfo={product.nutritionalInfo}
                        productInstruction={product.cms.productInstruction}
                      />
                    </div>
                  )}
                  {product.subProducts?.length === 1 && (
                    <div className="p-4 md:pl-4 lg:pl-16">
                      <ProductInfo
                        nutritionalInfo={
                          product.subProducts[0].product.nutritionalInfo
                        }
                        productInstruction={
                          product.subProducts[0].product.cms.productInstruction
                        }
                        servingsPerPackageOverride={bundleServingsPerPackage}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* SubProducts */}
              {product.hasSubProducts && (
                <div>
                  <SubProductsContainer
                    cmsSubProducts={product.cms.subProductsNew}
                    mode={draftCart.mode}
                  />
                  {/* <div className="hidden md:block">
                <OtherProductsContainer
                  products={reccomendProducts}
                  slidesPerView={3.5}
                />
              </div> */}
                </div>
              )}

              {/* MOBILE - Others also bought */}
              {/* <div className="block md:hidden">
            <OtherProductsContainer products={reccomendProducts} />
          </div> */}
            </div>

            {/* MOBILE - Add to Cart */}

            <div
              className={`fixed bottom-0 left-0 z-50 flex w-full flex-col justify-end bg-white shadow-3xl md:hidden md:justify-center`}
            >
              <div className="flex w-full flex-col items-center">
                {showProductInfoCard && (
                  <MobileProductInfoCard product={product} />
                )}
                {showRecurringEducation && (
                  <ShippingEducation
                    showProductInfoCard={showProductInfoCard}
                    action={action as Action}
                  />
                )}
                {renderButtons()}
                {isRestricted && (
                  <div className="flex-1 pb-4 font-interMedium text-xs">
                    Unavailable for delivery to
                    <span
                      className="ml-2 text-attention underline"
                      onClick={handleRestrictedCheck}
                    >
                      {deliveryAddress}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <AuthModal
              isOpen={isAuthModalOpen}
              onRequestClose={() => setAuthModalOpen(false)}
              navigatedFrom={path}
              forceRedirectUrl={path}
            />
          </>
        )}
      </Main>
    );
  },
);

export const getStaticProps: GetStaticProps = async (context: any) => {
  const { slug } = context.params!;
  const store = initializeStore({ ssr: true });

  const cachedProductData = getCachedProduct(slug);
  if (cachedProductData) {
    console.info(`PDP getStaticProps - cache found product for slug: ${slug}`);
    return {
      props: {
        product: cachedProductData,
      },
    };
  }

  const { products }: { products: ProductModelType[] } =
    await store.api.queryProducts(
      {
        where: {
          status: {
            _eq: ProductStatus.ACTIVE,
          },
          slug: {
            _eq: slug,
          },
        },
        skipVisibleCheck: true,
        imgixParams: {
          auto: 'format',
        },
      },
      PRODUCT_PDP_SELECTOR,
    );

  const product = products?.[0];
  console.info(`PDP getStaticProps - cache missing for slug: ${slug}`);
  if (!product) {
    return {
      props: {
        product: null,
      },
    };
  }
  // If product not found redirect to the menu
  const cleanProduct = JSON.parse(JSON.stringify(product));
  return {
    props: {
      product: cleanProduct,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const store = initializeStore({ ssr: true });

  const productLookup = await cacheAllProductsPDPData();
  if (productLookup) {
    console.info('PDP getStaticPaths - using cached product data');
    const paths = Object.keys(productLookup)
      .map((sku) => {
        const product = productLookup[sku];
        if (!product?.slug) {
          console.warn(
            `PDP getStaticPaths - cache slug not found for sku: ${sku}`,
          );
          return null;
        }
        return {
          params: { slug: product.slug },
        };
      })
      .filter((path) => !!path);
    return {
      paths,
      fallback: true,
    };
  }
  console.info('PDP getStaticPaths - no cache');

  const { products }: { products: ProductModelType[] } =
    await store.api.queryProducts(
      {
        where: {
          status: {
            _eq: ProductStatus.ACTIVE,
          },
        },
        skipVisibleCheck: true,
      },
      SLUG_SELECTOR,
    );

  const paths = products.map((product) => {
    return {
      params: { slug: product.slug },
    };
  });

  return {
    paths,
    fallback: true,
  };
};

export default ProductDetails;
